/**
 * Global footer rendered on every page.
 *
 * Variants
 *   - "full" (default): all secondary links — Journal, Terms, Privacy,
 *     Cookie preferences, Contact, Security, Sign in / Sign up.
 *     Used on marketing / public pages.
 *   - "slim": tighter strip with just the legal essentials —
 *     Terms, Privacy, Cookie preferences, Contact.
 *     Used inside the authed app where the main nav already covers
 *     Sign in / Journal / Security.
 *
 * The "Sign in" link is hidden automatically when the user is logged in.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_budget-forecast-27/artifacts/w1b6b3hu_mini.png';

const Footer = ({ variant = 'full' }) => {
  const { user } = useAuth();
  const slim = variant === 'slim';

  const openCookieSettings = () => {
    window.dispatchEvent(new Event('forge:open-cookie-settings'));
  };

  return (
    <footer
      className="border-t border-[#E2E8F0] bg-white mt-8 print-hide"
      data-testid={`global-footer-${variant}`}
    >
      <div
        className={`max-w-6xl mx-auto px-6 sm:px-8 ${slim ? 'py-5' : 'py-8'} flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-sm text-[#64748B]`}
      >
        <div className="flex items-center gap-2">
          <img
            src={LOGO_URL}
            alt="Forge Ledger"
            className="w-5 h-5 rounded object-cover"
          />
          <span>© {new Date().getFullYear()} Forge Ledger. Built in Britain.</span>
        </div>

        <div className="flex items-center gap-x-5 gap-y-2 flex-wrap">
          {!slim && (
            <Link to="/blog" className="hover:text-[#020617] transition-colors" data-testid="global-footer-journal">
              Journal
            </Link>
          )}
          <Link to="/about#about-section-terms" className="hover:text-[#020617] transition-colors" data-testid="global-footer-terms">
            Terms
          </Link>
          <Link to="/about#about-section-security" className="hover:text-[#020617] transition-colors" data-testid="global-footer-privacy">
            Privacy
          </Link>
          <button
            type="button"
            onClick={openCookieSettings}
            className="hover:text-[#020617] transition-colors"
            data-testid="global-footer-cookies"
          >
            Cookie preferences
          </button>
          <Link to="/contact" className="hover:text-[#020617] transition-colors" data-testid="global-footer-contact">
            Contact us
          </Link>
          {!slim && (
            <Link to="/security" className="hover:text-[#020617] transition-colors" data-testid="global-footer-security">
              Security
            </Link>
          )}
          {!slim && !user && (
            <Link to="/login" className="hover:text-[#020617] transition-colors" data-testid="global-footer-login">
              Sign in
            </Link>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
